// Enhances a scroll-snap carousel: builds pagination dots + keeps the active one
// in sync, and adds mouse/pen drag-to-scroll (scroll-snap alone isn't mouse-
// draggable). Wrap a `[data-carousel-track]` (slide children) and an empty
// `[data-carousel-dots]`. Progressive enhancement — the track scrolls/snaps and
// touch-swipes without JS; this adds dots + click-drag.
if (!customElements.get("carousel-dots")) {
  customElements.define(
    "carousel-dots",
    class CarouselDots extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector("[data-carousel-track]");
        this.dotsContainer = this.querySelector("[data-carousel-dots]");
        this.slides = this.track ? Array.from(this.track.children) : [];
        if (!this.track || this.slides.length <= 1) return;

        if (this.dotsContainer) {
          this.buildDots();
          this.observer = new IntersectionObserver(this.onIntersect, {
            root: this.track,
            threshold: 0.6,
          });
          this.slides.forEach((slide) => this.observer.observe(slide));
        }

        // Drag-to-scroll (mouse/pen). Touch keeps native scrolling.
        this.track.addEventListener("pointerdown", this.onPointerDown);
        this.track.addEventListener("dragstart", this.preventDrag);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.track?.removeEventListener("pointerdown", this.onPointerDown);
        this.track?.removeEventListener("dragstart", this.preventDrag);
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
      }

      buildDots() {
        // Each consuming section styles its own dots — pass a class via
        // `data-dot-class` on the [data-carousel-dots] element. Defaults to the
        // original cards-info class so existing usage is unchanged.
        const dotClass = this.dotsContainer.dataset.dotClass || "cards-info__dot";
        this.dots = this.slides.map((_, index) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = dotClass;
          dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
          dot.addEventListener("click", () => {
            this.slides[index].scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          });
          this.dotsContainer.appendChild(dot);
          return dot;
        });
        this.setActive(0);
      }

      onIntersect = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActive(this.slides.indexOf(entry.target));
          }
        });
      };

      setActive(activeIndex) {
        this.dots?.forEach((dot, index) => {
          dot.classList.toggle("is-active", index === activeIndex);
        });
      }

      preventDrag = (event) => event.preventDefault();

      onPointerDown = (event) => {
        // Let touch use native scrolling; only drag for mouse/pen, primary button.
        if (event.pointerType === "touch" || event.button !== 0) return;
        this.dragging = true;
        this.startX = event.clientX;
        this.startScroll = this.track.scrollLeft;
        this.track.classList.add("is-dragging");
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
      };

      onPointerMove = (event) => {
        if (!this.dragging) return;
        this.track.scrollLeft = this.startScroll - (event.clientX - this.startX);
      };

      onPointerUp = () => {
        this.dragging = false;
        this.track.classList.remove("is-dragging");
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
      };
    },
  );
}
